/**
 * Dock et panneaux rattachés – portage de design/components/bundle.js (Dock, OpenPages, AiAssistant, ShortcutPicker).
 * Panneau du dock = Radix Popover (non modal) ancré sur son bouton : position et bec calculés par Radix (centré sur le
 * bouton, gardé à 12 px des bords de l'écran), Échap ou clic à côté pour fermer, focus placé dans le panneau à
 * l'ouverture et rendu au bouton à la fermeture, Tab reste dans le panneau.
 */
import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cx, h, Icon, ModuleTag, type IconName, type Metier, type Status } from './base';
import { ATTRIBUT_MODALE } from './dialogues';

/* ---------- Dock (menu du bas) ---------- */
/**
 * Ajouts AGORA : 'notifs' = panneau Notifications (bouton cloche, juste avant le Profil) ; 'tel' = panneau Téléphone
 * (softphone) ; 'scribe' = panneau Scribe (enregistreur de réunions, juste avant la cloche) ; 'mail' = messagerie
 * « mini » (enveloppe).
 */
export type CibleDock = 'ai' | 'pages' | 'mods' | 'mail' | 'tel' | 'scribe' | 'notifs' | 'me';
/** Ajout AGORA : '__add' = bouton « + » du mode personnalisation. */
export type CibleDockEtendue = CibleDock | '__add';
/**
 * Favori du dock. Ajout AGORA : `badgeLabel`, sens du badge pour les lecteurs d'écran (ex. « En retard : 2 ») – le nom
 * du bouton devient « libellé · badgeLabel » ; le chiffre du badge est dessiné par CSS (`data-n`), pas du texte du bouton
 * (WCAG 2.5.3 : le nom contient le texte affiché, comme les pastilles Pages et Notifications).
 */
export interface DockFavori { id: string; label: string; icon: IconName; metier?: Metier; badge?: number | string; badgeLabel?: string }
export interface DockProps {
  popover?: { target: CibleDockEtendue; content: React.ReactNode; width?: number | string } | null;
  ariaLabel?: string; aiLabel: string; onAi?: () => void; aiOpen?: boolean;
  favorites: DockFavori[]; activeId?: string; onSelect?: (id: string) => void; max?: number;
  pagesLabel?: string; pagesCount?: number; pagesOpen?: boolean; onPages?: () => void;
  /** Ajout AGORA : nom accessible du bouton Pages avec le nombre de pages (ex. « Pages · Ouvertes : 2 »). */
  pagesAriaLabel?: string;
  modulesLabel?: string; onModules?: () => void; homeLabel?: string; onHome?: () => void; homeActive?: boolean;
  userOpen?: boolean; initials: string; userLabel: string; onUser?: () => void; compact?: boolean;
  editing?: boolean; addLabel?: string; doneLabel?: string; removeLabel?: string; onAdd?: () => void; onRemove?: (id: string) => void; onDone?: () => void;
  className?: string;
  /** Ajouts AGORA : appui long → mode personnalisation ; référence de l'hôte (fermeture au clic extérieur). */
  onLongPress?: () => void; hostRef?: React.Ref<HTMLDivElement>;
  /**
   * Ajout AGORA : bouton « Notifications » (cloche) juste avant le Profil, avec la pastille du nombre de non lues
   * (style du compteur des Pages, « 9+ » au-delà de 9) ; son panneau sort du bouton (popover.target = 'notifs').
   * notificationsAriaLabel : nom accessible complet (ex. « Notifications · Non lues : 3 »), la pastille étant masquée
   * aux technologies d'assistance. Sans notificationsLabel, pas de bouton.
   */
  notificationsLabel?: string; notificationsAriaLabel?: string; notificationsCount?: number; notificationsOpen?: boolean;
  onNotifications?: (e: React.MouseEvent) => void;
  /**
   * Ajout AGORA (27/10/2026) : bouton « Messagerie » (enveloppe) juste avant les Notifications, qui ouvre la messagerie
   * « mini » (panneau : popover.target = 'mail', `mailOpen`) ; pastille des non lus comme la cloche ; `mailActive`
   * quand l'écran de la messagerie est affiché. mailAriaLabel : nom accessible complet (ex. « Messagerie · Non lus : 5 »).
   * Sans mailLabel, pas de bouton.
   */
  mailLabel?: string; mailAriaLabel?: string; mailCount?: number; mailActive?: boolean; mailOpen?: boolean;
  onMail?: (e: React.MouseEvent) => void;
  /**
   * Ajout AGORA : bouton « Téléphone » (softphone) entre la messagerie et la cloche, avec une pastille d'état (couleur du statut,
   * masquée aux technologies d'assistance : l'état est dans phoneAriaLabel, ex. « Téléphone · Disponible ») ; bouton
   * cerclé quand un appel sonne (phoneRinging) ; son panneau sort du bouton (popover.target = 'tel'). Sans phoneLabel,
   * pas de bouton.
   */
  phoneLabel?: string; phoneAriaLabel?: string; phoneOpen?: boolean; phoneStatus?: Status; phoneRinging?: boolean;
  onPhone?: () => void;
  /**
   * Ajout AGORA (30/10/2026) : bouton « Scribe » (enregistreur de réunions) entre le Téléphone et la cloche ; son panneau
   * sort du bouton (popover.target = 'scribe', `scribeOpen`). Pendant un enregistrement (`scribeRecording`) : bouton rouge
   * (--danger), point qui clignote (fixe si le mouvement est réduit) et temps écoulé (`scribeTime`, « 00:42:15 ») à la
   * place du libellé – dessiné depuis `data-temps`, pas du texte du bouton : `scribeAriaLabel` porte le nom complet (ex.
   * « Scribe prend des notes · 00:42:15 »), aussi en infobulle. `scribeStatus` : pastille d'état hors enregistrement (envoi,
   * réunion à reprendre, hors ligne). `scribeIcon` : icône du nœud du catalogue. Sans scribeLabel, pas de bouton.
   * `scribePaused` (lot 2) : enregistrement en pause (silence de 60 s) – bouton toujours rouge, point fixe et estompé, temps
   * arrêté ; le nom le dit (« Scribe en pause · 00:42:15 »).
   */
  scribeLabel?: string; scribeAriaLabel?: string; scribeOpen?: boolean; scribeRecording?: boolean; scribeTime?: string; scribePaused?: boolean;
  scribeStatus?: Status; scribeIcon?: IconName; onScribe?: () => void;
  /**
   * Ajout AGORA : pastille sur l'avatar (dock compact trop étroit pour la cloche) et nom accessible du bouton Profil.
   * Le nom commence par les initiales affichées (WCAG 2.5.3 : le nom contient le texte visible, pour la commande
   * vocale) ; par défaut « initiales · userLabel » (ex. « VB · Vincent Barbier »).
   */
  userCount?: number; userAriaLabel?: string;
  /**
   * Ajout AGORA : fermeture demandée par le panneau (Échap, clic ou focus en dehors du dock et du panneau). Un clic sur
   * un bouton du dock reste traité par ce bouton (bascule ou changement de panneau).
   */
  onPopoverClose?: () => void;
}

/** Écart entre le haut de la barre du dock et le bas du panneau (référence : 16 px). */
const ECART_PANNEAU = 16;

/**
 * Pastille de compteur du dock (Pages, Notifications, avatar) : « 9+ » au-delà de 9, masquée aux lecteurs d'écran (le
 * nombre est dans le nom accessible du bouton). Le chiffre est dessiné par CSS (`data-n`, app.css) : ce n'est pas du
 * texte du bouton, sinon il se colle à son libellé (« 3Notifications ») et le nom ne contient plus le texte visible.
 */
const pastille = (n?: number) => (n ? h('b', { className: 'ag-dock-n', 'aria-hidden': true, 'data-n': n > 9 ? '9+' : String(n) }) : null);

interface OptionsBouton {
  tag?: 'div' | null; cls?: string | false; on?: boolean; pops?: boolean; noLabel?: boolean; onClick?: (e: React.MouseEvent) => void;
  content?: React.ReactNode; badge?: number | string; remove?: React.ReactNode; ariaLabel?: string;
  /** Ajouts AGORA : infobulle (par défaut le libellé) ; élément affiché à la place du libellé (temps de Scribe). */
  title?: string; labelNode?: React.ReactNode;
}

export function Dock(p: DockProps) {
  const editing = !!p.editing, favs = p.favorites || [], max = p.max || 5;
  const pop = p.popover || null, target = pop ? pop.target : null;
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  // Ancre du panneau : le bouton visé ; repli AGORA si le bouton est absent (ex. « + » masqué quand 5 favoris) → la
  // barre du dock (panneau centré sur le dock). Le panneau s'arrête à ECART_PANNEAU px au-dessus de la barre, comme la
  // référence : l'écart Radix part du bouton, on y ajoute donc la marge haute de la barre.
  const ancre = React.useRef<HTMLElement | null>(null);
  const [decalage, setDecalage] = React.useState(ECART_PANNEAU);
  // Focus rendu au bouton du panneau fermé, sauf fermeture par un clic ou un focus ailleurs (le focus y reste).
  const dernier = React.useRef<CibleDockEtendue | null>(null);
  const ailleurs = React.useRef(false);
  React.useLayoutEffect(() => {
    const nav = hostRef.current?.querySelector('nav') ?? null;
    ancre.current = target ? hostRef.current?.querySelector<HTMLElement>(`[data-dock="${target}"]`) ?? nav : null;
    if (ancre.current && nav) setDecalage(Math.round(ancre.current.getBoundingClientRect().top - nav.getBoundingClientRect().top) + ECART_PANNEAU);
    if (target) dernier.current = target;
  }, [target, p.compact, editing]);

  // Appui long (≈ 0,6 s) sur le dock → personnalisation des favoris (charte « Menu du bas »).
  const minuteur = React.useRef<number | undefined>(undefined);
  const appui = p.onLongPress && !editing ? {
    onPointerDown: () => { window.clearTimeout(minuteur.current); minuteur.current = window.setTimeout(() => p.onLongPress?.(), 600); },
    onPointerUp: () => window.clearTimeout(minuteur.current),
    onPointerLeave: () => window.clearTimeout(minuteur.current),
    onContextMenu: (e: React.MouseEvent) => { if (minuteur.current) e.preventDefault(); },
  } : {};

  const btn = (key: string, icon: IconName | null, label: string | undefined, o: OptionsBouton = {}) => {
    const cible = key === target;
    return h(o.tag || 'button', {
      key, 'data-dock': key, type: o.tag ? undefined : 'button',
      className: cx('ag-dock-btn', o.cls, (o.on || cible) && 'ag-dock-on', cible && 'ag-dock-target'),
      'aria-current': o.on ? 'page' : undefined,
      'aria-expanded': o.pops
        ? cible || (key === 'ai' && !!p.aiOpen) || (key === 'notifs' && !!p.notificationsOpen) || (key === 'tel' && !!p.phoneOpen) || (key === 'scribe' && !!p.scribeOpen) || (key === 'mail' && !!p.mailOpen)
        : undefined,
      'aria-haspopup': o.pops ? (key === 'me' ? 'menu' : 'dialog') : undefined,
      'aria-label': o.ariaLabel || (p.compact ? label : undefined), title: o.title || label, onClick: o.onClick,
    },
    o.content || (icon ? h(Icon, { name: icon, size: 22 }) : null),
    !p.compact && !o.noLabel ? o.labelNode || h('span', { className: 'ag-dock-label' }, label) : null,
    o.badge && !editing ? h('span', { className: 'ag-tab-count', 'aria-hidden': true, 'data-n': String(o.badge) }) : null,
    o.remove || null);
  };

  const fav = favs.slice(0, max).map((f) => btn(f.id, f.icon, f.label, {
    on: f.id === p.activeId, badge: f.badge, tag: editing ? 'div' : null, cls: editing && 'ag-dock-wiggle',
    ariaLabel: f.badge && !editing ? [f.label, f.badgeLabel].filter(Boolean).join(' · ') : undefined,
    content: h('span', { className: 'ag-dock-ico' }, h(Icon, { name: f.icon, size: 22 }), f.metier ? h('i', { className: cx('ag-dock-dot', 'ag-mod-' + f.metier) }) : null),
    onClick: () => { if (!editing) p.onSelect?.(f.id); },
    remove: editing ? h('button', {
      type: 'button', className: 'ag-tab-remove', 'aria-label': `${p.removeLabel || ''} ${f.label}`.trim(),
      onClick: (e: React.MouseEvent) => { e.stopPropagation(); p.onRemove?.(f.id); },
    }, h(Icon, { name: 'minus', size: 14 })) : null,
  }));
  if (editing && favs.length < max) fav.push(btn('__add', 'plus', p.addLabel, { cls: 'ag-dock-add', onClick: p.onAdd }));

  const nav = h('nav', {
    className: cx('ag-dock', 'ag-glass', p.compact && 'ag-dock-compact', editing && 'ag-dock-editing', target && 'ag-dock-popping', p.className),
    'aria-label': p.ariaLabel, ...appui,
  },
  btn('ai', 'spark', p.aiLabel, {
    pops: true, noLabel: true, cls: cx('ag-dock-ai', (p.aiOpen || target === 'ai') && 'ag-dock-ai-open'), onClick: p.onAi,
    content: h('span', { className: 'ag-dock-ai-ico' }, h(Icon, { name: 'spark', size: 20 })),
  }),
  h('span', { className: 'ag-dock-sep', 'aria-hidden': true }),
  fav,
  h('span', { className: 'ag-dock-sep', 'aria-hidden': true }),
  editing ? btn('__done', 'check', p.doneLabel, { cls: 'ag-dock-done', onClick: p.onDone }) : [
    btn('pages', 'layers', p.pagesLabel, {
      pops: true, onClick: p.onPages, on: p.pagesOpen, ariaLabel: p.pagesAriaLabel,
      content: h('span', { className: 'ag-dock-ico' }, h(Icon, { name: 'layers', size: 22 }), pastille(p.pagesCount)),
    }),
    p.modulesLabel ? btn('mods', 'grid', p.modulesLabel, { pops: true, onClick: p.onModules }) : null,
    p.homeLabel ? btn('home', 'home', p.homeLabel, { onClick: p.onHome, on: p.homeActive }) : null,
    p.mailLabel ? btn('mail', 'mail', p.mailLabel, {
      pops: true, onClick: p.onMail, on: p.mailActive, cls: p.mailOpen && 'ag-dock-on', ariaLabel: p.mailAriaLabel,
      content: h('span', { className: 'ag-dock-ico' }, h(Icon, { name: 'mail', size: 22 }), pastille(p.mailCount)),
    }) : null,
    p.phoneLabel ? btn('tel', 'telephone', p.phoneLabel, {
      pops: true, onClick: p.onPhone, cls: cx(p.phoneOpen && 'ag-dock-on', p.phoneRinging && 'agx-dock-tel-sonne'), ariaLabel: p.phoneAriaLabel,
      content: h('span', { className: 'ag-dock-ico' }, h(Icon, { name: 'telephone', size: 22 }),
        h('i', { className: cx('agx-dock-tel-etat', `agx-dock-tel-${p.phoneStatus || 'neutral'}`), 'aria-hidden': true })),
    }) : null,
    p.scribeLabel ? btn('scribe', p.scribeIcon || 'edit', p.scribeLabel, {
      pops: true, onClick: p.onScribe, ariaLabel: p.scribeAriaLabel, title: p.scribeAriaLabel,
      cls: cx(p.scribeOpen && 'ag-dock-on', p.scribeRecording && 'agx-dock-scribe-actif', p.scribeRecording && p.scribePaused && 'agx-dock-scribe-pause'),
      // Temps écoulé dessiné par CSS (data-temps) : le nom accessible le porte déjà.
      labelNode: p.scribeRecording && p.scribeTime
        ? h('span', { className: 'ag-dock-label agx-dock-scribe-temps', 'aria-hidden': true, 'data-temps': p.scribeTime })
        : undefined,
      content: h('span', { className: 'ag-dock-ico' }, h(Icon, { name: p.scribeIcon || 'edit', size: 22 }),
        p.scribeRecording ? h('i', { className: 'agx-dock-scribe-point', 'aria-hidden': true })
          : p.scribeStatus ? h('i', { className: cx('agx-dock-tel-etat', `agx-dock-tel-${p.scribeStatus}`), 'aria-hidden': true }) : null),
    }) : null,
    p.notificationsLabel ? btn('notifs', 'bell', p.notificationsLabel, {
      pops: true, onClick: p.onNotifications, cls: p.notificationsOpen && 'ag-dock-on', ariaLabel: p.notificationsAriaLabel,
      content: h('span', { className: 'ag-dock-ico' }, h(Icon, { name: 'bell', size: 22 }), pastille(p.notificationsCount)),
    }) : null,
    btn('me', null, p.userLabel, {
      pops: true, noLabel: true, cls: cx('ag-dock-me', p.userOpen && 'ag-dock-on'), onClick: p.onUser,
      ariaLabel: p.userAriaLabel || [p.initials, p.userLabel].filter(Boolean).join(' · '),
      content: p.userCount
        ? h('span', { className: 'ag-dock-ico' }, h('span', { className: 'ag-avatar' }, p.initials), pastille(p.userCount))
        : h('span', { className: 'ag-avatar' }, p.initials),
    }),
  ]);

  const lierHote = (el: HTMLDivElement | null) => {
    hostRef.current = el;
    if (typeof p.hostRef === 'function') p.hostRef(el);
    else if (p.hostRef) (p.hostRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
  };
  // Contrairement à la référence (qui ne crée l'hôte qu'avec un panneau), l'hôte est toujours présent :
  // l'arbre DOM reste stable à l'ouverture / fermeture des panneaux (pas de remontage du dock).
  return h('div', { className: 'ag-dock-host', ref: lierHote },
    h(Popover.Root, { open: !!pop, onOpenChange: (o: boolean) => { if (!o) p.onPopoverClose?.(); } },
      h(Popover.Anchor, { virtualRef: ancre }),
      pop ? h(Popover.Content, {
        // Les panneaux gardent leur propre rôle (dialog, section nommée, menu) : le conteneur n'en ajoute pas.
        role: undefined, className: 'ag-dock-pop', style: { width: pop.width },
        side: 'top', align: 'center', sideOffset: decalage, collisionPadding: 12, arrowPadding: 16,
        onOpenAutoFocus: () => { ailleurs.current = false; },
        // Échap dans une liste avec recherche ouverte (Combobox du panneau) : seule la liste se referme.
        onEscapeKeyDown: (e: KeyboardEvent) => {
          if ((e.target as Element | null)?.closest?.('[role="combobox"][aria-expanded="true"]')) e.preventDefault();
        },
        onInteractOutside: (e: Event) => {
          const cible = e.target as Element | null;
          // Bouton du dock : il gère lui-même la bascule. Fenêtre modale ouverte depuis le panneau (ex. confirmation de
          // fermeture d'une page) : le panneau reste ouvert dessous.
          if (hostRef.current?.contains(cible) || cible?.closest?.(`[${ATTRIBUT_MODALE}]`)) e.preventDefault();
          else ailleurs.current = true;
        },
        onCloseAutoFocus: (e: Event) => {
          e.preventDefault();
          if (!ailleurs.current) hostRef.current?.querySelector<HTMLElement>(`[data-dock="${dernier.current}"]`)?.focus();
        },
      },
      pop.content,
      h(Popover.Arrow, { asChild: true }, h('i', { className: 'ag-dock-tail', 'aria-hidden': true }))) : null),
    nav);
}

/* ---------- OpenPages (pages ouvertes) ---------- */
export interface OpenPagesProps {
  title: string; hint?: string; closeLabel?: string; activeId?: string;
  pages: { id: string; metier: Metier; moduleLabel: string; title: string; state?: string; time?: string }[];
  /** Ajouts AGORA : reprendre / fermer une page. */
  onOpen?: (id: string) => void; onClose?: (id: string) => void;
  /** Ajouts AGORA : pages visibles (côte à côte), bouton « + » pour afficher une page à côté de la page active. */
  visibleIds?: string[]; splitLabel?: string; onSplit?: (id: string) => void;
}

export function OpenPages(p: OpenPagesProps) {
  const cartes = React.useRef<HTMLDivElement | null>(null);
  // Flèches : passer d'une page à l'autre (charte : « flèches pour choisir, Entrée pour reprendre »).
  const fleches = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const liste = Array.from(cartes.current?.querySelectorAll<HTMLElement>('.ag-page-card') || []);
    const i = liste.indexOf(document.activeElement as HTMLElement);
    const suivant = liste[(i + (e.key === 'ArrowRight' ? 1 : -1) + liste.length) % liste.length];
    if (suivant) { e.preventDefault(); suivant.focus(); }
  };
  return h('section', { className: cx('ag-pages', 'ag-glass', 'ag-liquid'), 'aria-label': p.title, onKeyDown: fleches },
    h('div', { className: 'ag-pages-head' }, h('b', null, p.title), p.hint ? h('span', null, p.hint) : null),
    h('div', { className: 'ag-pages-row', ref: cartes }, (p.pages || []).map((pg) => {
      const on = pg.id === p.activeId;
      const visible = on || (p.visibleIds || []).includes(pg.id);
      return h('article', {
        key: pg.id, className: cx('ag-page-card', visible && 'ag-page-on'), tabIndex: 0, 'aria-current': on ? 'page' : undefined,
        onClick: () => p.onOpen?.(pg.id),
        onKeyDown: (e: React.KeyboardEvent) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); p.onOpen?.(pg.id); } },
      },
      h('div', { className: 'ag-page-top' }, h(ModuleTag, { metier: pg.metier, label: pg.moduleLabel }),
        h('span', { className: 'agx-page-btns' },
          p.onSplit && !on ? h('button', {
            type: 'button', className: 'ag-page-x', 'aria-label': `${p.splitLabel || ''} ${pg.title}`.trim(), title: p.splitLabel,
            onClick: (e: React.MouseEvent) => { e.stopPropagation(); p.onSplit?.(pg.id); },
          }, h(Icon, { name: 'plus', size: 14 })) : null,
          h('button', {
            type: 'button', className: 'ag-page-x', 'aria-label': `${p.closeLabel || ''} ${pg.title}`.trim(), title: p.closeLabel,
            onClick: (e: React.MouseEvent) => { e.stopPropagation(); p.onClose?.(pg.id); },
          }, h(Icon, { name: 'close', size: 14 })))),
      h('div', { className: 'ag-page-title' }, pg.title),
      pg.state ? h('div', { className: 'ag-page-state' }, pg.state) : null,
      pg.time ? h('div', { className: 'ag-page-time' }, pg.time) : null);
    })));
}

/* ---------- AiAssistant (chat IA) ---------- */
export interface AiAssistantProps {
  anchored?: boolean; title: string; contextLabel?: string; messages: { from: 'user' | 'ai'; text: string }[]; suggestions?: string[];
  placeholder: string; sendLabel?: string; closeLabel?: string; disclaimer?: string;
  /** Ajouts AGORA. */
  onClose?: () => void; onSend?: (texte: string) => void; onSuggestion?: (texte: string) => void;
}

export function AiAssistant(p: AiAssistantProps) {
  const [texte, setTexte] = React.useState('');
  const envoyer = () => { if (texte.trim() && p.onSend) { p.onSend(texte.trim()); setTexte(''); } };
  return h('section', { className: cx('ag-ai', 'ag-glass', 'ag-glass-strong', p.anchored && 'ag-ai-anchored'), role: 'dialog', 'aria-label': p.title },
    h('header', { className: 'ag-ai-head' }, h('span', { className: 'ag-dock-ai-ico' }, h(Icon, { name: 'spark', size: 18 })), h('b', null, p.title),
      h('button', { type: 'button', className: 'ag-page-x', 'aria-label': p.closeLabel, onClick: p.onClose }, h(Icon, { name: 'close', size: 14 }))),
    p.contextLabel ? h('div', { className: 'ag-ai-ctx' }, h(Icon, { name: 'layers', size: 14 }), p.contextLabel) : null,
    h('div', { className: 'ag-ai-msgs', 'aria-live': 'polite' }, (p.messages || []).map((m, i) =>
      h('div', { key: i, className: cx('ag-ai-msg', m.from === 'user' ? 'ag-ai-user' : 'ag-ai-bot') }, m.text))),
    p.suggestions ? h('div', { className: 'ag-ai-sugg' }, p.suggestions.map((x, i) =>
      h('button', { key: i, type: 'button', className: 'ag-chip', onClick: () => p.onSuggestion?.(x) }, x))) : null,
    h('div', { className: 'ag-ai-input' },
      h('input', {
        placeholder: p.placeholder, 'aria-label': p.placeholder, value: texte,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTexte(e.target.value),
        onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') envoyer(); },
      }),
      h('button', { type: 'button', className: 'ag-ai-send', 'aria-label': p.sendLabel, onClick: envoyer }, h(Icon, { name: 'send', size: 18 }))),
    p.disclaimer ? h('div', { className: 'ag-ai-disc' }, p.disclaimer) : null);
}

/* ---------- ShortcutPicker (catalogue Métiers › Modules) ---------- */
export interface ShortcutPickerProps {
  title: string; countLabel?: string;
  groups: { metier: Metier; label: string; items: { id: string; label: string; icon: IconName; pinned?: boolean }[] }[];
  onToggle?: (id: string) => void;
  /**
   * Extension AGORA – mode « ouvrir » : la tuile ouvre le module (onToggle) et une punaise distincte, dans la tuile,
   * l'épingle ou la retire des favoris du dock (onPin). Sans onPin : comportement du design (la tuile bascule le favori).
   */
  onPin?: (id: string) => void;
  pinLabel?: string;
  unpinLabel?: string;
  /** Message bref sous l'en-tête (ex. « Retirez d'abord un favori »). */
  notice?: string;
}

export function ShortcutPicker(p: ShortcutPickerProps) {
  const tuile = (it: ShortcutPickerProps['groups'][number]['items'][number]) => {
    if (!p.onPin) {
      return h('button', { key: it.id, type: 'button', className: 'ag-sc', 'aria-pressed': !!it.pinned, onClick: () => p.onToggle?.(it.id) },
        it.pinned ? h('span', { className: 'ag-sc-check' }, h(Icon, { name: 'check', size: 12 })) : null,
        h(Icon, { name: it.icon }), it.label);
    }
    const action = `${it.pinned ? p.unpinLabel : p.pinLabel} : ${it.label}`;
    return h('div', { key: it.id, className: 'agx-sc-case' },
      h('button', { type: 'button', className: 'ag-sc', onClick: () => p.onToggle?.(it.id) }, h(Icon, { name: it.icon }), it.label),
      h('button', {
        type: 'button', className: 'agx-sc-epingle', 'aria-pressed': !!it.pinned, 'aria-label': action, title: action,
        onClick: (e: React.MouseEvent) => { e.stopPropagation(); p.onPin?.(it.id); },
      }, h(Icon, { name: 'punaise', size: 14 })));
  };
  return h('section', { className: 'ag-sheet ag-glass ag-glass-strong', 'aria-label': p.title },
    h('div', { className: 'ag-sheet-grab' }),
    h('div', { className: 'ag-sheet-head' }, h('h3', null, p.title), h('span', null, p.countLabel)),
    p.notice ? h('p', { className: 'agx-sc-avis', role: 'status' }, p.notice) : null,
    (p.groups || []).map((g) =>
      h('div', { key: g.metier, className: 'ag-sc-group' }, h(ModuleTag, { metier: g.metier, label: g.label }),
        h('div', { className: 'ag-sc-grid' }, g.items.map(tuile)))));
}
